import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface LiteratureNoteFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

export default function LiteratureNoteForm({ onClose, onSuccess, editData }: LiteratureNoteFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    authors_year: '',
    source: '',
    rq_question: '',
    rq_importance: '',
    method_name: '',
    data_info: '',
    result_numbers: '',
    author_interpretation: '',
    reference_points: '',
    differences: '',
    usage_method: '',
    author_limitations: '',
    my_limitations: '',
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showHelp, setShowHelp] = useState<{ [key: string]: boolean }>({});
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  useEffect(() => {
    if (editData) {
      // 旧形式との互換性を保つ
      setFormData({
        title: editData.title || '',
        authors_year: editData.authors_year || '',
        source: editData.source || '',
        rq_question: editData.rq_question || editData.paper_rq || '',
        rq_importance: editData.rq_importance || '',
        method_name: editData.method_name || editData.method_data || '',
        data_info: editData.data_info || '',
        result_numbers: editData.result_numbers || editData.key_findings || '',
        author_interpretation: editData.author_interpretation || '',
        reference_points: editData.reference_points || editData.connection_to_my_research || '',
        differences: editData.differences || '',
        usage_method: editData.usage_method || '',
        author_limitations: editData.author_limitations || editData.limitations || '',
        my_limitations: editData.my_limitations || '',
      });
      setResearchTheme(editData.research_theme || '');
    }
    fetchAvailableThemes();
  }, [editData]);

  const fetchAvailableThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('research_overview')
        .select('theme')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const themes = data?.map(item => item.theme).filter(Boolean) || [];
      setAvailableThemes(themes);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  const toggleHelp = (field: string) => {
    setShowHelp(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDelete = async () => {
    if (!editData?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('literature_notes')
        .delete()
        .eq('id', editData.id);

      if (error) throw error;

      setNotification({ type: 'success', message: '先行研究メモを削除しました' });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('削除エラー:', error);
      setNotification({ type: 'error', message: '削除に失敗しました' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!formData.title || !formData.authors_year || !formData.reference_points) {
      setNotification({ type: 'error', message: 'タイトル、著者・年、参考にする点は必須です' });
      return;
    }

    setLoading(true);

    try {
      const dataToSave = {
        user_id: user.id,
        research_theme: researchTheme || null,
        ...formData,
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('literature_notes')
          .update(dataToSave)
          .eq('id', editData.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '先行研究メモを更新しました' });
      } else {
        const { error } = await supabase.from('literature_notes').insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '先行研究メモを保存しました' });
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        else onClose();
      }, 1500);
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-book-line text-blue-600"></i>
          {editData ? '参考文献メモを編集' : '参考文献メモを追加'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* 基本情報セクション */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border-l-4 border-blue-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-file-text-line text-blue-600"></i>基本情報
          </h3>

          {/* 関連テーマ */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                関連テーマ
              </label>
            </div>
            <select
              value={researchTheme}
              onChange={(e) => setResearchTheme(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            >
              <option value="">テーマを選択（任意）</option>
              {availableThemes.map((theme, index) => (
                <option key={index} value={theme}>{theme}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">「テーマと目的」で登録したテーマを選択できます</p>
          </div>

          {/* タイトル */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                論文タイトル <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('title')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.title && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>読んだ論文の正式なタイトルを記入します。</p>
                <p className="font-semibold mb-1">💡 記入例</p>
                <p>「事業承継税制が中小企業の承継行動に与える影響：パネルデータ分析」</p>
              </div>
            )}

            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              placeholder="例：事業承継税制が中小企業の承継行動に与える影響：パネルデータ分析"
              required
            />
          </div>

          {/* 著者・年 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                著者・年 <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('authors')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.authors && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>論文の著者名と発表年を記入します。</p>
              </div>
            )}

            <input
              type="text"
              value={formData.authors_year}
              onChange={(e) => setFormData({ ...formData, authors_year: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              placeholder="例：山田太郎・佐藤花子 (2023)"
              required
            />
          </div>

          {/* 出典 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                出典（URL/DOI）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('source')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.source && (
              <div className="mb-3 p-3 bg-white border border-blue-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>論文にアクセスできるURLやDOIを記録します。</p>
              </div>
            )}

            <input
              type="text"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              placeholder="https://doi.org/10.xxxx/xxxxx"
            />
          </div>
        </div>

        {/* 論文のRQセクション */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border-l-4 border-purple-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-question-line text-purple-600"></i>論文のRQ（リサーチクエスチョン）
          </h3>

          {/* この論文が答えようとしている問い */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                この論文が答えようとしている問い
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('rq_question')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.rq_question && (
              <div className="mb-3 p-3 bg-white border border-purple-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この論文が明らかにしようとしている中心的な問いを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.rq_question}
              onChange={(e) => setFormData({ ...formData, rq_question: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              rows={2}
              placeholder="〇〇は△△にどのような影響を与えるか？"
            />
          </div>

          {/* なぜその問いが重要か */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                なぜその問いが重要か
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('rq_importance')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.rq_importance && (
              <div className="mb-3 p-3 bg-white border border-purple-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この問いを明らかにすることの学術的・社会的意義を記入します。</p>
              </div>
            )}

            <textarea
              value={formData.rq_importance}
              onChange={(e) => setFormData({ ...formData, rq_importance: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
              rows={3}
              placeholder="この問いを明らかにすることで、〇〇に貢献できる"
            />
          </div>
        </div>

        {/* 方法・データセクション */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 sm:p-6 border-l-4 border-teal-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-flask-line text-teal-600"></i>方法・データ
          </h3>

          {/* 分析手法 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                分析手法
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('method_name')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.method_name && (
              <div className="mb-3 p-3 bg-white border border-teal-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この論文で使われている分析手法の名称を記入します。</p>
              </div>
            )}

            <input
              type="text"
              value={formData.method_name}
              onChange={(e) => setFormData({ ...formData, method_name: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              placeholder="例：差分の差分法（DID）"
            />
          </div>

          {/* 使用データ */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                使用データ（名称・期間・サンプルサイズ）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('data_info')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 hover:bg-teal-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.data_info && (
              <div className="mb-3 p-3 bg-white border border-teal-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>どのデータを使ったか、期間とサンプルサイズを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.data_info}
              onChange={(e) => setFormData({ ...formData, data_info: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
              rows={2}
              placeholder="〇〇調査、△△年〜□□年、約××社/人のデータ"
            />
          </div>
        </div>

        {/* 主要な発見セクション */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 sm:p-6 border-l-4 border-amber-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-line text-amber-600"></i>主要な発見
          </h3>

          {/* 主要な結果 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                主要な結果（数値・方向性）
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('result_numbers')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.result_numbers && (
              <div className="mb-3 p-3 bg-white border border-amber-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>分析の結果得られた具体的な数値や効果の方向性を記入します。</p>
              </div>
            )}

            <textarea
              value={formData.result_numbers}
              onChange={(e) => setFormData({ ...formData, result_numbers: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇が1単位増加すると、△△が□□%/年変化する（有意水準：p<0.05）"
            />
          </div>

          {/* 著者の解釈・結論 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                著者の解釈・結論
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('author_interpretation')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.author_interpretation && (
              <div className="mb-3 p-3 bg-white border border-amber-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>著者がこの結果をどう解釈し、どのような結論を導いているかを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.author_interpretation}
              onChange={(e) => setFormData({ ...formData, author_interpretation: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              rows={3}
              placeholder="著者は、この結果から〇〇であると解釈し、△△と結論づけている"
            />
          </div>
        </div>

        {/* 自分の研究との関連セクション */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-links-line text-green-600"></i>自分の研究との関連
          </h3>

          {/* 参考にする点 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                参考にする点 <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('reference_points')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.reference_points && (
              <div className="mb-3 p-3 bg-white border border-green-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この論文から何を学び、どの部分を参考にするかを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.reference_points}
              onChange={(e) => setFormData({ ...formData, reference_points: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              rows={3}
              placeholder="この論文の〇〇が参考になる。特に△△の部分は自分の研究でも活用できる。"
              required
            />
          </div>

          {/* 自分の研究との違い */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                自分の研究との違い
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('differences')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.differences && (
              <div className="mb-3 p-3 bg-white border border-green-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この論文と自分の研究の違いを明確にします。独自性を示す重要なポイントです。</p>
              </div>
            )}

            <textarea
              value={formData.differences}
              onChange={(e) => setFormData({ ...formData, differences: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              rows={3}
              placeholder="この論文は〇〇を扱っているが、本研究では△△を分析する点が異なる"
            />
          </div>

          {/* 具体的な活用方法 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                具体的な活用方法
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('usage_method')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.usage_method && (
              <div className="mb-3 p-3 bg-white border border-green-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>この論文の知見を自分の研究でどう活かすか、具体的なアクションを記入します。</p>
              </div>
            )}

            <textarea
              value={formData.usage_method}
              onChange={(e) => setFormData({ ...formData, usage_method: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇の方法を第△章で実装する。□□として引用する。"
            />
          </div>
        </div>

        {/* 限界セクション */}
        <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-4 sm:p-6 border-l-4 border-rose-500">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-alert-line text-rose-600"></i>限界
          </h3>

          {/* 著者が述べている限界 */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                著者が述べている限界
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('author_limitations')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.author_limitations && (
              <div className="mb-3 p-3 bg-white border border-rose-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>論文中で著者自身が認めている限界や課題を記入します。</p>
              </div>
            )}

            <textarea
              value={formData.author_limitations}
              onChange={(e) => setFormData({ ...formData, author_limitations: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
              rows={3}
              placeholder="著者は、〇〇が限界であると述べている"
            />
          </div>

          {/* 自分が感じた限界・疑問点 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">
                自分が感じた限界・疑問点
              </label>
              <button
                type="button"
                onClick={() => toggleHelp('my_limitations')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            
            {showHelp.my_limitations && (
              <div className="mb-3 p-3 bg-white border border-rose-200 rounded-lg text-xs text-slate-700">
                <p className="font-semibold mb-1">📌 何を書く？</p>
                <p>読んでいて自分が感じた疑問点や、改善できそうな点を記入します。</p>
              </div>
            )}

            <textarea
              value={formData.my_limitations}
              onChange={(e) => setFormData({ ...formData, my_limitations: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
              rows={3}
              placeholder="〇〇が考慮されていない。△△にバイアスがある可能性がある。"
            />
          </div>
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {editData && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              削除
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {loading ? '保存中...' : editData ? '更新する' : '保存'}
          </button>
        </div>
      </form>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 sm:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6">この先行研究メモを削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full sm:flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="w-full sm:flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {loading ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
